import type { Session } from "@supabase/supabase-js";
import { LoaderCircle, ShieldX } from "lucide-react";
import { useEffect, useState } from "react";

import { AuthView } from "@/components/AuthView";
import { Newsroom } from "@/components/Newsroom";
import { bootstrapNewsroom } from "@/lib/newsroom";
import { supabase } from "@/lib/supabase";

export function App() {
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [accessError, setAccessError] = useState("");

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setIsLoading(false);
    });
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      setSession(nextSession);
      setAccessError("");
      setIsLoading(false);
    });
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (!session) {
      return;
    }
    setIsLoading(true);
    bootstrapNewsroom()
      .catch((error: unknown) => {
        setAccessError(
          error instanceof Error
            ? error.message
            : "This account does not have newsroom access",
        );
      })
      .finally(() => setIsLoading(false));
  }, [session?.user.id]);

  if (isLoading) {
    return (
      <main className="loading-screen">
        <LoaderCircle aria-hidden="true" />
        <span>Opening the private desk…</span>
      </main>
    );
  }
  if (!session) {
    return <AuthView />;
  }
  if (accessError) {
    return (
      <main className="access-denied">
        <ShieldX aria-hidden="true" />
        <h1>Access is not configured for this account.</h1>
        <p>{accessError}</p>
        <button type="button" onClick={() => void supabase.auth.signOut()}>
          Sign out
        </button>
      </main>
    );
  }
  return <Newsroom ownerId={session.user.id} />;
}
